// client/src/services/devTeamApi.ts
//
// Thin client for the bug-report / Dev Team board backend. Reads hit admin-hq
// direct-SQL endpoints; the submit + status/assign writes hop through
// func-shared-services (the sole writer). Each call throws on a non-OK response
// so callers can fall back to the local preview store (BugReportsContext).
import { BugReport, BugStatus, NewReport } from '../contexts/BugReportsContext';

// The ContextApp marker func uses for a merge parent (group) row. A card whose
// contextApp matches this is a group container, not a real app's report.
export const GROUP_CONTEXT_APP = 'dev-team-group';

// ── server → client shape ──────────────────────────────────────────────────

// Board only surfaces four statuses; map the shared schema's extra ones so
// cross-app reports (which may be 'triaged' / 'wont_fix') still lay out.
function normStatus(s: string): BugStatus {
  switch ((s || '').toLowerCase()) {
    case 'inbox_triage': return 'inbox_triage';
    case 'in_progress': return 'in_progress';
    case 'resolved': return 'resolved';
    case 'wont_fix': return 'resolved';
    case 'triaged': return 'open';
    default: return 'open';
  }
}

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function mapServerReport(row: any): BugReport {
  return {
    bugReportId: Number(row.bugReportId),
    contextApp: row.contextApp || 'admin-hq',
    issue: row.title || row.doing || '(untitled)',
    description: row.happened || undefined,
    steps: row.repro || undefined,
    submittedBy: row.submittedBy || 'unknown',
    status: normStatus(row.status),
    assignedTo: row.assignedTo || null,
    when: relativeTime(row.createdAt),
    // For the board's derived "archived" state: when the report was resolved.
    // func stamps ReviewedAt on resolve; fall back to CreatedAt if absent.
    resolvedAt: row.reviewedAt || row.createdAt || null,
    // Free-form dev-team notes (any status) + provenance.
    notes: row.notes ?? null,
    notesUpdatedBy: row.notesUpdatedBy || null,
    notesUpdatedAt: row.notesUpdatedAt || null,
    threadCount: Number(row.threadCount) || 0,
    // Merge grouping: parent (group) row id this card is filed under, or null.
    // A group parent row itself has contextApp === GROUP_CONTEXT_APP.
    parentId: row.parentId != null ? Number(row.parentId) : null,
    isGroup: (row.contextApp || '') === GROUP_CONTEXT_APP,
    attachments: Array.isArray(row.attachments) ? row.attachments : [],
  };
}

// A file attachment captured with an inbox-triage email (bytes live in blob;
// this is just the ref — download goes through /api/dev-team?mode=attachment).
export type AttachmentRef = {
  id: string;
  name: string;
  contentType: string | null;
  size: number | null;
};

// One rolled-up email reply in a thread (from ContextJson.thread[]).
export type ThreadMessage = {
  from: string | null;
  receivedAt: string | null;
  subject: string | null;
  body: string | null;
  messageId: string | null;
  attachments?: AttachmentRef[];
};

// Browser-hittable download URL for one attachment (GET → streams the file).
export function attachmentDownloadUrl(bugReportId: number, attachmentId: string): string {
  return `/api/dev-team?mode=attachment&bugReportId=${encodeURIComponent(bugReportId)}&attachmentId=${encodeURIComponent(attachmentId)}`;
}

async function getJson(url: string): Promise<any> {
  const r = await fetch(url, { headers: { accept: 'application/json' }, credentials: 'same-origin' });
  const text = await r.text();
  let data: any = {};
  try { data = text ? JSON.parse(text) : {}; } catch { /* non-JSON */ }
  if (!r.ok || data?.error) throw new Error(data?.error || `${url} → ${r.status}`);
  return data;
}

async function postJson(url: string, body: unknown): Promise<any> {
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body || {}),
  });
  const text = await r.text();
  let data: any = {};
  try { data = text ? JSON.parse(text) : {}; } catch { /* non-JSON */ }
  if (!r.ok || data?.error) throw new Error(data?.error || `${url} → ${r.status}`);
  return data;
}

// ── Board (admin) ────────────────────────────────────────────────────────────

export async function fetchBoardReports(): Promise<BugReport[]> {
  const data = await getJson('/api/dev-team?mode=list');
  return (data.reports || []).map(mapServerReport);
}

export async function fetchAssignees(): Promise<string[]> {
  const data = await getJson('/api/dev-team?mode=assignees');
  return data.emails || [];
}

// The rolled-up email replies for one report (lazy-loaded when the card's thread
// button is clicked). Empty array if the report has no thread.
export async function fetchThread(bugReportId: number): Promise<ThreadMessage[]> {
  const data = await getJson(`/api/dev-team?mode=thread&bugReportId=${encodeURIComponent(bugReportId)}`);
  return (data.thread || []) as ThreadMessage[];
}

// status-only change omits assignedTo (leaves it untouched); assign passes the
// report's current status (the resolve contract always needs a valid status).
export async function updateReport(
  bugReportId: number,
  patch: { status: BugStatus; assignedTo?: string | null },
): Promise<void> {
  const data = await postJson('/api/dev-team', { mode: 'update', bugReportId, ...patch });
  // func returns { ok, resolved:[...] }. An empty `resolved` means the row wasn't
  // updated — almost always because the cross-app triage authority isn't deployed
  // to func-shared-services yet, so admin-hq's resolve is still scoped to its own
  // ContextApp and silently skips other apps' reports. Treat that as a failure so
  // the board reverts immediately (instead of faking success then snapping back).
  if (Array.isArray(data?.resolved) && data.resolved.length === 0) {
    throw new Error('The shared service accepted the request but updated 0 reports — cross-app triage isn\'t enabled on func-shared-services yet.');
  }
}

// Hard-delete a report (used by "Delete"). func returns { ok, deleted }.
export async function deleteReport(bugReportId: number): Promise<void> {
  const data = await postJson('/api/dev-team', { mode: 'delete', bugReportId });
  if (typeof data?.deleted === 'number' && data.deleted === 0) {
    throw new Error('The shared service deleted 0 reports — cross-app triage isn\'t enabled on func-shared-services yet.');
  }
}

// Email the reporter of a bug report (admin-only). Goes to admin-hq's own
// /api/dev-team, which relays to the shared Send-Email Logic App, sent as the
// logged-in admin so replies come back to them. Throws on a non-OK response so the composer can show the error.
export async function emailSender(input: { bugReportId?: number; to: string; subject: string; body: string }): Promise<void> {
  await postJson('/api/dev-team', { mode: 'email', ...input });
}

// Save the dev-team's free-form notes on a report (any status). Relays to func
// (sole writer); pass '' to clear. Throws on a non-OK response.
export async function saveNotes(bugReportId: number, notes: string): Promise<void> {
  await postJson('/api/dev-team', { mode: 'notes', bugReportId, notes });
}

// Merge cards under a new parent (group) card. func creates the parent row and
// re-parents the children (min 2). `status` is the lane the group lands in.
export async function mergeReports(input: { name: string; status: BugStatus; childIds: number[] }): Promise<void> {
  await postJson('/api/dev-team', { mode: 'merge', ...input });
}

// Dissolve a group — children become top-level again, the parent row is deleted.
export async function ungroupReport(parentId: number): Promise<void> {
  await postJson('/api/dev-team', { mode: 'ungroup', parentId });
}

// ── Reporter ──────────────────────────────────────────────────────────────────

export async function fetchMyReports(): Promise<BugReport[]> {
  const data = await getJson('/api/my-bug-reports');
  return (data.reports || []).map(mapServerReport);
}

// Submit → func-shared-services /api/bug-report (via the proxy). Maps the modal's
// Issue/Description/Steps to the shared schema's doing/happened/repro + title.
// Returns the new BugReportId (the user-facing reference #).
export async function submitReport(r: NewReport): Promise<number> {
  const data = await postJson('/api/bug-report', {
    title: (r.issue || '').slice(0, 200),
    description: { doing: r.issue, happened: r.description || '', repro: r.steps || '' },
    context: r.context || {},
    logs: r.logs || [],
    events: r.events || [],
  });
  return Number(data.bugReportId);
}
